/**
 * Painel de acesso ao app do responsável — status + convite (reusa InviteResultDialog).
 */
import { useState, type CSSProperties } from 'react';
import {
  Smartphone, Mail, MessageCircle, Copy, CheckCircle2, Send, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { ROUTES } from '../../api/routes';
import { InviteResultDialog } from '../../components/invite/InviteResultDialog';

export type ResponsavelAcessoUsuario = {
  id: string;
  statusConta?: string | null;
  tokenExpiraEm?: string | null;
  ativadoEm?: string | null;
  email?: string | null;
};

export type ResponsavelAcessoData = {
  id: string;
  nome: string;
  email?: string | null;
  telefone?: string | null;
  cpf?: string | null;
  dataNascimento?: string | null;
  usuarioId?: string | null;
  usuario?: ResponsavelAcessoUsuario | null;
  _count?: { alunos?: number };
  alunos?: unknown[];
};

export type AppAcessoStatusKey = 'sem_conta' | 'pendente' | 'expirado' | 'ativa' | 'suspensa';

export type AppAcessoStatus = {
  key: AppAcessoStatusKey;
  label: string;
  color: string;
  bg: string;
  border: string;
  /** Pode disparar /convidar */
  canInvite: boolean;
};

export function getResponsavelAppStatus(r: ResponsavelAcessoData): AppAcessoStatus {
  const u = r.usuario;
  if (!r.usuarioId && !u) {
    return {
      key: 'sem_conta',
      label: 'Sem conta',
      color: '#6b7280',
      bg: '#f3f4f6',
      border: '#d1d5db',
      canInvite: true,
    };
  }
  const s = u?.statusConta;
  if (s === 'ATIVA') {
    return {
      key: 'ativa',
      label: 'App ativo',
      color: '#16a34a',
      bg: '#dcfce7',
      border: '#86efac',
      canInvite: false,
    };
  }
  if (s === 'SUSPENSA') {
    return {
      key: 'suspensa',
      label: 'Suspensa',
      color: '#6b7280',
      bg: '#f3f4f6',
      border: '#d1d5db',
      canInvite: false,
    };
  }
  const expira = u?.tokenExpiraEm ? new Date(u.tokenExpiraEm).getTime() : null;
  const expirado = expira != null && expira <= Date.now();
  if (expirado) {
    return {
      key: 'expirado',
      label: 'Convite expirado',
      color: '#dc2626',
      bg: '#fee2e2',
      border: '#fecaca',
      canInvite: true,
    };
  }
  if (s === 'PENDENTE' || s === 'ATIVANDO') {
    return {
      key: 'pendente',
      label: s === 'ATIVANDO' ? 'Em ativação' : 'Convite pendente',
      color: '#d97706',
      bg: '#fef3c7',
      border: '#fde68a',
      canInvite: true,
    };
  }
  return {
    key: 'sem_conta',
    label: 'Sem conta',
    color: '#6b7280',
    bg: '#f3f4f6',
    border: '#d1d5db',
    canInvite: true,
  };
}

export function AppStatusBadge({ status }: { status: AppAcessoStatus }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: '0.64rem',
        fontWeight: 700,
        background: status.bg,
        color: status.color,
        border: `1px solid ${status.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {status.key === 'ativa' && <CheckCircle2 size={11} />}
      {status.key === 'expirado' && <AlertTriangle size={11} />}
      {status.label}
    </span>
  );
}

type InviteResult = {
  activationLink: string;
  whatsappLink?: string | null;
  emailEnviado?: boolean;
  emailError?: string | null;
  codigoDisplay?: string | null;
};

type Props = {
  responsavel: ResponsavelAcessoData;
  escolaId: string;
  canManage: boolean;
  compact?: boolean;
  onInvited?: () => void;
};

export function ResponsavelAcessoAppPanel({
  responsavel,
  escolaId,
  canManage,
  compact = false,
  onInvited,
}: Props) {
  const status = getResponsavelAppStatus(responsavel);
  const [busy, setBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteResult, setInviteResult] = useState<InviteResult | null>(null);

  const alunoCount = responsavel._count?.alunos ?? responsavel.alunos?.length ?? 0;
  const gaps: string[] = [];
  if (!responsavel.email?.trim()) gaps.push('e-mail');
  if (!responsavel.cpf?.trim()) gaps.push('CPF');
  if (!responsavel.dataNascimento) gaps.push('data de nascimento');
  if (alunoCount === 0) gaps.push('aluno vinculado');

  const isRecovery = status.key === 'ativa';
  const canCallInvite =
    canManage && status.canInvite && !!responsavel.email?.trim() && alunoCount > 0;
  const canCallRecovery =
    canManage && isRecovery && !!responsavel.email?.trim();

  const gerarLink = async (): Promise<InviteResult | null> => {
    if (isRecovery) {
      if (!canCallRecovery) {
        toast.error('Cadastre o e-mail do responsável para reenviar o acesso.');
        return null;
      }
    } else if (!canCallInvite) {
      if (!responsavel.email?.trim()) toast.error('Cadastre o e-mail do responsável para convidar.');
      else if (alunoCount === 0) toast.error('Vincule ao menos um aluno antes de convidar.');
      return null;
    }

    const path = isRecovery ? 'reenviar-acesso' : 'convidar';
    setBusy(true);
    try {
      const { data } = await api.post(
        `${ROUTES.responsaveis.findOne(escolaId, responsavel.id)}/${path}`,
      );
      const normalized: InviteResult = {
        activationLink: data.activationLink || data.recoveryLink,
        whatsappLink: data.whatsappLink,
        emailEnviado: data.emailEnviado,
        emailError: data.emailError,
        codigoDisplay: data.codigoDisplay,
      };
      setInviteResult(normalized);
      setInviteOpen(true);
      onInvited?.();
      return normalized;
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          (isRecovery ? 'Erro ao reenviar acesso' : 'Erro ao gerar convite'),
      );
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleEmail = async () => {
    await gerarLink();
  };

  const handleWhatsApp = async () => {
    const d = await gerarLink();
    if (!d) return;
    if (d.whatsappLink) window.open(d.whatsappLink, '_blank');
  };

  const handleCopy = async () => {
    await gerarLink();
  };

  const canCallActions = isRecovery ? canCallRecovery : canCallInvite;

  const btnSt = (bg: string): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: compact ? '5px 10px' : '6px 12px',
    borderRadius: 8,
    fontSize: compact ? '0.68rem' : '0.72rem',
    fontWeight: 700,
    cursor: busy ? 'wait' : 'pointer',
    border: 'none',
    background: bg,
    color: '#fff',
    opacity: busy ? 0.65 : 1,
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
  });

  return (
    <>
      <div
        style={{
          borderRadius: 10,
          border: '1px solid #e2e8f0',
          background: compact ? 'transparent' : '#f8fafc',
          padding: compact ? 0 : 12,
          display: 'flex',
          flexDirection: 'column',
          gap: compact ? 8 : 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Smartphone size={14} style={{ color: '#64748b' }} />
          <strong style={{ fontSize: '0.72rem', color: '#334155' }}>Acesso ao app</strong>
          <AppStatusBadge status={status} />
        </div>

        {!canManage ? (
          <p style={{ margin: 0, fontSize: '0.7rem', color: '#64748b', lineHeight: 1.45 }}>
            {status.key === 'ativa'
              ? 'Conta ativa no aplicativo.'
              : status.key === 'pendente' || status.key === 'expirado'
                ? 'Aguardando ativação pelo responsável.'
                : status.key === 'suspensa'
                  ? 'Conta suspensa.'
                  : 'Sem conta no aplicativo ainda.'}
          </p>
        ) : status.key === 'suspensa' ? (
          <p style={{ margin: 0, fontSize: '0.7rem', color: '#64748b', lineHeight: 1.45 }}>
            Conta suspensa — reative o usuário antes de enviar novo convite.
          </p>
        ) : (
          <>
            {isRecovery ? (
              <p style={{ margin: 0, fontSize: '0.7rem', color: '#64748b', lineHeight: 1.45 }}>
                Conta ativa. Se o responsável perdeu o acesso, reenvie um link de recuperação
                (válido por 15 minutos) para ele cadastrar uma nova senha.
              </p>
            ) : (
              gaps.length > 0 && (
                <div
                  style={{
                    fontSize: '0.68rem',
                    color: '#92400e',
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    borderRadius: 8,
                    padding: '8px 10px',
                    fontWeight: 600,
                  }}
                >
                  Complete antes do convite: {gaps.join(', ')}.
                  {!responsavel.cpf?.trim() || !responsavel.dataNascimento
                    ? ' CPF e data de nascimento são usados no quiz de ativação.'
                    : ''}
                </div>
              )
            )}

            <div>
              <div
                style={{
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  color: '#64748b',
                  marginBottom: 6,
                }}
              >
                {isRecovery
                  ? 'Reenviar acesso via:'
                  : status.key === 'pendente' || status.key === 'expirado'
                    ? 'Reenviar convite via:'
                    : 'Enviar convite via:'}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  disabled={busy || !canCallActions}
                  onClick={() => void handleEmail()}
                  style={btnSt(
                    canCallActions
                      ? 'linear-gradient(135deg, #6c5ce7, #a29bfe)'
                      : '#94a3b8',
                  )}
                  title={!responsavel.email?.trim() ? 'Cadastre o e-mail' : 'Enviar e-mail'}
                >
                  <Mail size={12} /> E-mail
                </button>
                <button
                  type="button"
                  disabled={busy || !canCallActions}
                  onClick={() => void handleWhatsApp()}
                  style={btnSt(canCallActions ? '#25D366' : '#94a3b8')}
                >
                  <MessageCircle size={12} /> WhatsApp
                </button>
                <button
                  type="button"
                  disabled={busy || !canCallActions}
                  onClick={() => void handleCopy()}
                  style={btnSt(
                    canCallActions
                      ? 'linear-gradient(135deg, #2563eb, #60a5fa)'
                      : '#94a3b8',
                  )}
                >
                  <Copy size={12} /> Abrir link / QR
                </button>
                {busy && (
                  <span style={{ fontSize: '0.68rem', color: '#94a3b8', alignSelf: 'center' }}>
                    <Send size={11} style={{ marginRight: 4 }} />
                    Gerando…
                  </span>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {inviteResult && (
        <InviteResultDialog
          open={inviteOpen}
          nome={responsavel.nome}
          email={responsavel.email || ''}
          perfil="RESPONSAVEL"
          telefone={responsavel.telefone || undefined}
          activationLink={inviteResult.activationLink}
          whatsappLink={inviteResult.whatsappLink}
          emailSent={!!inviteResult.emailEnviado}
          emailReason={inviteResult.emailError}
          kind={isRecovery ? 'recuperacao' : 'ativacao'}
          onClose={() => setInviteOpen(false)}
        />
      )}
    </>
  );
}
